import React from "react";
import { Plus, X } from "react-feather";
import { styled } from "styled-components";
import Button from "./Button";
import Label from "./Label";
import Description from "./Description";

interface Props<T> {
  component: React.FunctionComponent<{
    value: T;
    onChange: (value: T) => void;
  }>;
  extraProps?: any;
  value: T[];
  onChange: (value: T[]) => void;
  label?: React.ReactNode;
  newValue?: () => T;
  requireOne?: boolean;
  buttons?: React.ReactNode;
  labels?: React.ReactNode;
  required?: boolean;
  hasValue?: boolean;
  description?: string;
  /** Name of one row's item (e.g. "file name template") — when set, the default "Add" button
   *  reads "Add {noun}" instead of a bare "Add". Ignored if `buttons` overrides the button
   *  entirely. */
  noun?: string;
  /** Drops the outer `Wrapper`'s default `margin-bottom: 20px` — for call sites that embed a
   *  `ListInput` inline among other fields (rather than as its own standalone form field) and
   *  want to control the spacing after it themselves. */
  noMargin?: boolean;
}

const ListElement = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 5px;

  > div {
    flex-grow: 1;
  }
`;

const StyledButton = styled(Button)`
  margin-left: 5px;
`;

const Wrapper = styled.div<{ $noMargin?: boolean }>`
  margin-bottom: ${(p) => (p.$noMargin ? 0 : "20px")};
`;

const Buttons = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 5px;
  }
`;

const Header = styled.div`
  margin-bottom: 3px;
`;

const ListInput = <T,>(props: Props<T>) => {
  const {
    component,
    value: values,
    onChange,
    label,
    labels,
    newValue,
    requireOne,
    extraProps,
    buttons,
    required,
    hasValue,
    description,
    noMargin,
    noun,
  } = props;

  const canDelete = !requireOne || values.length > 1;
  const margin = canDelete ? 36 : 0;

  return (
    <Wrapper $noMargin={noMargin}>
      {label && <Label label={label} required={required} hasValue={hasValue} />}
      <Header style={{ marginRight: margin }}>{labels}</Header>
      {values.map((value, index) => (
        <ListElement key={index}>
          {React.createElement(component, {
            ...(extraProps || {}),
            value,
            onChange: (newValue) => {
              onChange(values.map((v, i) => (i === index ? newValue : v)));
            },
          })}
          {canDelete && (
            <StyledButton
              icon={X}
              type="danger"
              borderless
              iconSize={21}
              onClick={() => {
                onChange(values.filter((v, i) => i !== index));
              }}
            />
          )}
        </ListElement>
      ))}
      <Buttons>
        {buttons ? (
          buttons
        ) : (
          <Button
            label={noun ? `Add ${noun}` : `Add`}
            icon={Plus}
            type="success"
            onClick={() => {
              if (newValue) {
                onChange([...values, newValue()]);
              }
            }}
          />
        )}
      </Buttons>
      <Description label={description} />
    </Wrapper>
  );
};

export default ListInput;
